import type ApolloClient from 'apollo-client'
import { getOperationName } from 'apollo-link'
import { ActivitiesForDocDocument } from '@paintscout/api'
import { scheduleDelayedRefetch } from './scheduleDelayedRefetch'
import { QUOTE_SIDEBAR_INFO_QUERY_NAME } from './queryNames'

/**
 * Refreshes an estimate/invoice's activity surfaces after a mutation the server records as an
 * activity but does not return to us — currently a deal stage change made from the estimate.
 *
 * Two operation names, both needed. `activitiesForDoc` is QuoteActivity's list
 * (`apps/app/src/components/common/QuoteSidebar/pages/QuoteActivity.tsx`); `sidebarInfo` is
 * QuoteSidebar's one-row "last activity" line. Apollo matches the *operation* name, so refetching
 * `activitiesForDoc` does nothing for `sidebarInfo` even though `sidebarInfo` selects that field.
 * A name that matches no mounted query costs nothing — `scheduleDelayedRefetch` skips it.
 *
 * The refresh is deliberately delayed rather than declared in the mutation's `refetchQueries`.
 * `activitiesForDoc` is served from Elasticsearch, and `ActivityEntity.put` indexes the new
 * activity without `refresh`, so an immediate refetch races the index and can come back without the
 * row it was fired to fetch.
 *
 * Both names must resolve. A null entry is a silent no-op, which is the failure this whole class of
 * fix exists to prevent (#22137).
 */
export function scheduleQuoteActivityRefresh(client: ApolloClient<any>): void {
  const activitiesForDocName = getOperationName(ActivitiesForDocDocument)

  if (!activitiesForDocName) {
    throw new Error('scheduleQuoteActivityRefresh: could not resolve the activitiesForDoc operation name')
  }

  scheduleDelayedRefetch(client, [activitiesForDocName, QUOTE_SIDEBAR_INFO_QUERY_NAME])
}
